import React, { useEffect, useRef } from 'react';
import { createChart, CandlestickSeries, HistogramSeries, LineSeries } from 'lightweight-charts';

/**
 * TradingView-quality candlestick chart powered by lightweight-charts v5.
 * Renders OHLCV + SMA20/SMA50 + volume sub-pane.
 */
const LightweightCandleChart = ({ data = [] }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !data.length) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: '#0b0b14' }, textColor: '#cbd5e1' },
      grid: { vertLines: { color: '#1f2937' }, horzLines: { color: '#1f2937' } },
      timeScale: { timeVisible: true, borderColor: '#374151' },
      rightPriceScale: { borderColor: '#374151' },
      crosshair: { mode: 1 },
      // Force a valid BCP-47 locale — avoids Intl RangeError when the
      // container's LANG resolves to en-US@posix (Kubernetes default).
      localization: { locale: 'en-US' },
    });
    chartRef.current = chart;

    // Candles (v5 API: addSeries(SeriesType, options))
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e', downColor: '#ef4444',
      borderUpColor: '#22c55e', borderDownColor: '#ef4444',
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
    });
    const candleData = data.map((d) => ({ time: d.date, open: d.open, high: d.high, low: d.low, close: d.close }));
    candles.setData(candleData);

    // SMA20 line
    const sma20 = chart.addSeries(LineSeries, { color: '#10b981', lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    sma20.setData(data.filter((d) => d.sma20 != null).map((d) => ({ time: d.date, value: d.sma20 })));

    // SMA50 line
    const sma50 = chart.addSeries(LineSeries, { color: '#f59e0b', lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    sma50.setData(data.filter((d) => d.sma50 != null).map((d) => ({ time: d.date, value: d.sma50 })));

    // Volume histogram on its own price scale
    const volume = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      color: '#a855f7',
    });
    volume.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    volume.setData(data.map((d) => ({
      time: d.date,
      value: d.volume,
      color: d.close >= d.open ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)',
    })));

    chart.timeScale().fitContent();

    return () => {
      try { chart.remove(); } catch (_) { /* ignore */ }
      chartRef.current = null;
    };
  }, [data]);

  return (
    <div className="w-full rounded-lg overflow-hidden border border-purple-100" data-testid="lightweight-candle-chart">
      <div ref={containerRef} style={{ width: '100%', height: 360 }} />
    </div>
  );
};

export default LightweightCandleChart;
